import { AxiosResponse } from "axios";

import { axiosInstance } from "./common";

export enum SecurityRole {
  PRIMARY = "PRIMARY",
  COLLATERAL = "COLLATERAL",
}

export enum SecurityDistributionRights {
  EXCLUSIVE_CHARGE = "EXCLUSIVE_CHARGE",
  FIRST_PARI_PASSU = "FIRST_PARI_PASSU",
  SECOND_PARI_PASSU = "SECOND_PARI_PASSU",
  FIRST_CHARGE = "FIRST_CHARGE",
  SECOND_CHARGE = "SECOND_CHARGE",
  SUBSERVIENT = "SUBSERVIENT",
}

export enum SecurityChargeMode {
  HYPOTHECATION = "HYPOTHECATION",
  PLEDGE = "PLEDGE",
  LIEN = "LIEN",
  REGISTERED_MORTGAGE = "REGISTERED_MORTGAGE",
  EQUITABLE_MORTGAGE = "EQUITABLE_MORTGAGE",
}

export enum PublicChargeRegistry {
  CERSAI = "CERSAI",
  ROC = "ROC",
}

export const SECURITY_ROLE_LABELS: Record<SecurityRole,string> = {
  [SecurityRole.PRIMARY]: "Primary Security",
  [SecurityRole.COLLATERAL]: "Collateral Security",
};

export  const CHARGE_MODE_LABELS: Record<SecurityChargeMode,string> = {
  [SecurityChargeMode.REGISTERED_MORTGAGE]:"Registered Mortgage",
  [SecurityChargeMode.EQUITABLE_MORTGAGE]: "Equitable Mortgage",
  [SecurityChargeMode.HYPOTHECATION]: "Hypothecation",
  [SecurityChargeMode.PLEDGE]: "Pledge",
  [SecurityChargeMode.LIEN]: "Lien",
};

export const DISTRIBUTION_RIGHTS_LABELS: Record<SecurityDistributionRights,string> = {
  [SecurityDistributionRights.EXCLUSIVE_CHARGE]:"Exclusive",
  [SecurityDistributionRights.FIRST_PARI_PASSU]:"First Pari Passu",
  [SecurityDistributionRights.SECOND_PARI_PASSU]:"Second Pari Passu",
  [SecurityDistributionRights.FIRST_CHARGE]:"First Charge",
  [SecurityDistributionRights.SECOND_CHARGE]:"Second Charge",
  [SecurityDistributionRights.SUBSERVIENT]:"Subservient",
};

export const PUBLIC_CHARGE_REGISTRY_LABELS: Record<PublicChargeRegistry, string> = {
  [PublicChargeRegistry.CERSAI]: "CERSAI",
  [PublicChargeRegistry.ROC]: "ROC",
};

export interface RegistryIdentifiers {
    security_interest_id?: string;
    roc_charge_id?: string;
}

export interface ChargeFields {
  security_role?: SecurityRole;
  charge_mode?: SecurityChargeMode;
  distribution_rights?: SecurityDistributionRights;
  public_registry: PublicChargeRegistry;
  registry_identifiers: RegistryIdentifiers;
}

export interface Charge extends ChargeFields {
  charge_id: string;
  case_id: string;
}

export interface AssetLinkageFields {
  asset_id: string;
  charge_id: string;
}

export interface LoanLinkageFields {
  loan_reference_id: string;
  charge_id: string;
}

export interface AssetLinkage extends AssetLinkageFields {
  linkage_id: string;
}

export interface LoanLinkage extends LoanLinkageFields {
  linkage_id: string;
}

export interface GetChargesResponse {
  charges: Charge[];
  asset_linkages: AssetLinkage[];
  loan_linkages: LoanLinkage[];
}

export interface CreateChargeAssetLinkageFields {
  asset_id: string;
}

export interface CreateChargeLoanLinkageFields {
  loan_reference_id: string;
}

export interface CreateChargeRequest {
  charge: ChargeFields;
  asset_linkages: CreateChargeAssetLinkageFields[];
  loan_linkages: CreateChargeLoanLinkageFields[];
}

export interface AddAssetLinkagesRequest {
  asset_linkages: AssetLinkageFields[];
}

export interface AddAssetLinkagesResponse {
  asset_linkages: AssetLinkage[];
}

export interface AddLoanLinkagesRequest {
  loan_linkages: LoanLinkageFields[];
}

export interface AddLoanLinkagesResponse {
  loan_linkages: LoanLinkage[];
}

export interface ChargeWithLinkages {
  charges: Charge;
  asset_linkages: AssetLinkage[];
  loan_linkages: LoanLinkage[];
}

export interface MessageResponse {
  message: string;
}

export interface GetChargesParams {
  case_id: string;
}

export interface CreateChargeParams {
  case_id: string;
  request: CreateChargeRequest;
}

export interface AddAssetLinkagesParams {
  case_id: string;
  charge_id: string;
  request: AddAssetLinkagesRequest;
}

export interface AddLoanLinkagesParams {
  case_id: string;
  charge_id: string;
  request: AddLoanLinkagesRequest;
}

const ChargesAPI = {
  getCharges: async ( params: GetChargesParams) => {
    const response: AxiosResponse<GetChargesResponse> = await axiosInstance.get(
      `/v1/cases/${params.case_id}/charges`,
    );

    return response.data;
  },

  createCharge: async (params: CreateChargeParams) => {
    const response: AxiosResponse<ChargeWithLinkages> = await axiosInstance.post(
      `/v1/cases/${params.case_id}/charges`,
      params.request,
    );

    return response.data;
  },

  addAssetLinkages: async (params: AddAssetLinkagesParams) => {
    const response: AxiosResponse<AddAssetLinkagesResponse> = await axiosInstance.post(
        `/v1/cases/${params.case_id}/asset_linkages`,
        params.request,
      );

    return response.data;
  },

  addLoanLinkages: async (params: AddLoanLinkagesParams) => {
    const response: AxiosResponse<AddLoanLinkagesResponse> = await axiosInstance.post(
        `/v1/cases/${params.case_id}/loan_linkages`,
        params.request,
      );

    return response.data;
  },
};

export default ChargesAPI;